import clsx from 'clsx'
import constants from 'common/constants'
import moment from 'moment'
import React, {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link, useLocation, useNavigate} from 'react-router'
import {NotificationReducer, RootState} from 'setup'
import {
  getNotifications,
  getNotificationsCount,
  readAllNotifications,
  readNotifications,
} from 'setup/redux/Actions/Notifications/notifications'

import {KTSVG, toAbsoluteUrl} from '_metronic/helpers'

const NotificationItem: FC<{
  notification: any
  readNotification: (id: number | string) => void
}> = ({notification, readNotification}) => {
  const navigate = useNavigate()

  const notificationLink = notification.webUrl
    ? notification.webUrl.startsWith('/school') ||
      notification.webUrl.startsWith('school') ||
      notification.webUrl.startsWith('/institute') ||
      notification.webUrl.startsWith('institute')
      ? notification.webUrl
      : `/institute${
          notification.webUrl.startsWith('/') ? notification.webUrl : `/${notification.webUrl}`
        }`
    : '#'

  const handleClick = () => {
    navigate(notificationLink)
    readNotification(notification.id)
  }

  const cleanedDescription = notification.description
    ? notification.description.replace(/\\n/g, '\n')
    : ''

  return (
    <div className='d-flex flex-stack my-4 cursor-pointer' onClick={handleClick}>
      <div className='d-flex align-items-center'>
        <div className='symbol symbol-35px me-4'>
          <span className={clsx('symbol-label', `bg-light-${notification?.state ?? 'primary'}`)}>
            <KTSVG
              path={`/media/${notification?.icon ?? 'icons/duotune/general/gen044.svg'}`}
              className={`svg-icon-2 svg-icon-${notification?.state ?? 'primary'}`}
            />
          </span>
        </div>

        <div className='mb-0 me-2'>
          <Link to={notificationLink} className='fs-6 text-gray-800 text-hover-primary fw-bolder'>
            {notification.new && (
              <span className='bullet bullet-dot bg-success h-8px w-8px animation-blink me-1' />
            )}
            {notification.title}
          </Link>

          <div className='text-gray-400 fs-7'>
            {cleanedDescription
              .trim()
              .split('\n')
              .map((line: any, index: any, arr: any) => (
                <React.Fragment key={index}>
                  {line.trim()}
                  {index < arr.length - 1 && <br />}{' '}
                </React.Fragment>
              ))}
          </div>
        </div>
      </div>

      <span className='badge badge-light fs-8'>{moment(notification.date).fromNow()}</span>
    </div>
  )
}

const HeaderNotificationsMenu: FC = () => {
  const dropDownMenuRef = useRef<any>(null)
  const intl = useIntl()
  const [notifications, setNotifications] = useState([])
  const [notificationsCount, setNotificationsCount] = useState(0)
  const [allNotificationsRead, setAllNotificationsRead] = useState(false)

  const location = useLocation()
  const dispatch = useDispatch()

  const notificationsReducer = useSelector<RootState>(
    ({notificationsReducer}) => notificationsReducer,
    shallowEqual
  ) as NotificationReducer

  const loadNotifications = () => {
    dispatch(
      getNotifications({page: 1, pageSize: 100}, (res: any) => {
        setNotifications(res.result.data)
        setNotificationsCount(res.result.totalCount)
      })
    )
  }

  const loadNewNotificationsCount = () => {
    dispatch(getNotificationsCount())
  }

  const readNotification = (id: number | string) => {
    dispatch(readNotifications({notificationId: [id]}, notificationsCount - 1))
  }

  useEffect(() => {
    loadNotifications()
    loadNewNotificationsCount()
    const notificationsInterval = setInterval(() => {
      if (location.pathname !== '/dashboard') return
      loadNotifications()
    }, constants.notification_polling_delay)

    const notificationsCountInterval = setInterval(() => {
      if (location.pathname !== '/dashboard') return
      loadNewNotificationsCount()
    }, constants.notification_polling_delay)

    return () => {
      clearInterval(notificationsInterval)
      clearInterval(notificationsCountInterval)

      setNotifications([])
      setNotificationsCount(0)
      setAllNotificationsRead(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // * observer to watch change in notifications dropdown class name change add call read all notification to dropdown open
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          if (dropDownMenuRef.current.className.includes('show')) {
            const onSuccess = () => {
              setAllNotificationsRead(true)
            }

            if (notificationsReducer.notificationsCount > 0) {
              dispatch(readAllNotifications(onSuccess))
            }
          } else if (allNotificationsRead) {
            loadNotifications()
            loadNewNotificationsCount()
            setAllNotificationsRead(true)
          }
        }
      })
    })

    observer.observe(dropDownMenuRef.current, {attributes: true})

    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px'
      data-kt-menu='true'
      ref={dropDownMenuRef}
    >
      <div
        className='d-flex flex-column bgi-no-repeat rounded-top'
        style={{backgroundImage: `url('${toAbsoluteUrl('/media/misc/pattern-1.jpg')}')`}}
      >
        <h3 className='text-white fw-bold px-9 mt-10 mb-6'>
          {intl.formatMessage({id: 'NOTIFICATION.HEADING'})}
          <span className='fs-8 opacity-75 ms-3'>{notificationsCount}</span>
        </h3>
      </div>

      <div className='tab-content'>
        <div className='tab-pane fade show active' id='kt_topbar_notifications_1' role='tabpanel'>
          <div className='scroll-y mh-325px my-5 px-8'>
            {notifications.length < 1 && (
              <div>{intl.formatMessage({id: 'NOTIFICATION.NO_NEW_NOTIFICATION'})}</div>
            )}
            {notifications.map((notification: any, index) => (
              <NotificationItem
                notification={notification}
                readNotification={readNotification}
                key={index}
              />
            ))}
          </div>

          <div className='py-3 text-center border-top'>
            <Link
              to='/institute/notifications/admin'
              className='btn btn-color-gray-600 btn-active-color-primary'
            >
              {intl.formatMessage({id: 'GENERAL.VIEW_ALL'})}
              <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-5' />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export {HeaderNotificationsMenu}
